// src/components/page/article/ArticleMediaCarousel.tsx
'use client';

import type { Article } from '@/model/article';
import Image from 'next/image';

import type React from 'react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';

import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';
import LazyImage from '@/components/ui/lazyImage/LazyImage';
import styles from './ArticleMediaCarousel.module.scss';

/** 需要被忽略的心情圖 URL（列表 + 內頁都共用） */
const FEELING_IMAGE_URLS = new Set<string>([
  'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/feeling/feeling01.png',
  'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/feeling/feeling02.png',
  'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/feeling/feeling03.png',
  'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/feeling/feeling04.png',
  'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/feeling/feeling05.png',
  'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/feeling/feeling06.png',
  'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/feeling/feeling07.png',
  'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/feeling/feeling08.png',
]);

type Props = {
  article: Article;
  /** list：列表卡片版本；detail：內頁版本 */
  variant: 'list' | 'detail';
  /** 外層包裝的 className（會套在最外層 div，上層可以傳自己的 .imgWrapper） */
  className?: string;
};

type ShortDataItem = {
  cover?: string;
  crtime?: string;
  key?: string;
  ledtime?: string;
  short_id?: string;
  user_id?: string;
  video_url?: string;
};

type MediaMode = 'image' | 'short' | 'youtube' | 'tiktok' | 'music';

const LIST_PLAYER_HEIGHT = 300; // ✅ list 版：高度固定 300px
const SHORT_PLAYER_HEIGHT = 300; // 本機短影片高度（list 用）
const DEFAULT_SHORT_ASPECT = 9 / 16; // 預設直立短影音比例（寬 / 高）

const YOUTUBE_ASPECT = 16 / 9; // YouTube 常見比例
const TIKTOK_ASPECT = 9 / 16; // TikTok 常見直向比例

// 內頁圖片輪播框：避免高度 0/跳動/卡半屏
const DETAIL_IMAGE_MIN_H = 240;
const DETAIL_IMAGE_MAX_VH = 85;

/** 粗略從各種 YouTube 連結取出 videoId */
function extractYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);

    // https://youtu.be/xxxx
    if (u.hostname.includes('youtu.be')) {
      const path = u.pathname.replace('/', '');
      return path || null;
    }

    // https://www.youtube.com/watch?v=xxxx
    const v = u.searchParams.get('v');
    if (v) return v;

    // https://www.youtube.com/embed/xxxx
    const parts = u.pathname.split('/');
    const embedIndex = parts.findIndex((p) => p === 'embed');
    if (embedIndex >= 0 && parts[embedIndex + 1]) {
      return parts[embedIndex + 1] || null;
    }

    return null;
  } catch {
    return null;
  }
}

/** 抖音的 embed URL（後端只給 tiktokId 的情況） */
function buildTiktokEmbedUrl(idOrUrl: string): string {
  // 使用者若直接貼整個網址也支援
  if (idOrUrl.startsWith('http://') || idOrUrl.startsWith('https://')) {
    return idOrUrl;
  }
  // 簡單組成官方 embed v2
  return `https://www.tiktok.com/embed/v2/${encodeURIComponent(idOrUrl)}`;
}

/** 圖片清單（已排除 8 張心情圖） */
function getDisplayImages(article: Article): string[] {
  const covers = Array.isArray(article.covers) ? article.covers : [];
  const all = covers.length > 0 ? covers : article.cover ? [article.cover] : [];
  return all.filter((src) => src && !FEELING_IMAGE_URLS.has(src));
}

/**
 * 在這個元件裡自己解析 shortData，
 * 不管 transform 那邊是給陣列還是字串，都盡量吃進來。
 */
function normalizeShortData(raw: Article['shortData'] | undefined): ShortDataItem[] {
  if (!raw) return [];

  let list: unknown = raw;

  // 可能是 JSON 字串
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      list = parsed;
    } catch {
      return [];
    }
  }

  if (!Array.isArray(list)) return [];

  const normalized = (list as unknown[])
    .filter((item): item is ShortDataItem => {
      if (!item || typeof item !== 'object') return false;
      const obj = item as ShortDataItem;
      // 只要有 short_id 或 video_url 就認定是有效影片
      return !!(obj.short_id || obj.video_url);
    })
    .map((item) => ({ ...item }));

  return normalized;
}

/** 依 type + 欄位，決定要用哪一種媒體模式 */
function decideMediaMode(
  article: Article,
  flags: {
    hasImages: boolean;
    hasShortData: boolean;
    hasYoutube: boolean;
    hasTiktok: boolean;
    hasMusic: boolean;
  }
): MediaMode | null {
  const { hasImages, hasShortData, hasYoutube, hasTiktok, hasMusic } = flags;
  const rawType = (article.type || '').toLowerCase();

  // 🔥 1. 本機 shortData 優先（避免 type = img 時被 cover 搶走）
  if (hasShortData) return 'short';

  // 2. 再看後端 type
  if (rawType === 'img' && hasImages) return 'image';
  if (rawType === 'url' && (hasYoutube || hasTiktok)) {
    return hasYoutube ? 'youtube' : 'tiktok';
  }
  if (rawType === 'music' && hasMusic) return 'music';

  // 3. type 不可靠時，依欄位出現順序決定
  if (hasImages) return 'image';
  if (hasYoutube) return 'youtube';
  if (hasTiktok) return 'tiktok';
  if (hasMusic) return 'music';

  return null;
}

/**
 * ✅ 內頁圖片輪播框高度計算（重點修正）
 * - 我們不再「先猜 aspect」：改用 next/image 的 onLoadingComplete 拿到每張圖真實寬高
 * - 以「最高的那張」作為統一輪播框高度：
 *   width 固定 100% 時，高度 = containerWidth / (w/h)
 *   要高度最大 => 選 (w/h) 最小（minAspect）
 * - minAspect 會在圖片載入後逐步更新，框高也會跟著更新
 */
function useDetailImageFrame(enabled: boolean, minAspect: number) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [frameHeight, setFrameHeight] = useState<number>(Math.max(DETAIL_IMAGE_MIN_H, 320));

  const compute = useCallback(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    const el = ref.current;
    if (!el) return;

    const w = el.getBoundingClientRect().width;
    if (!w) return;

    const safeAspect = Number.isFinite(minAspect) && minAspect > 0 ? minAspect : 1;

    // 依「最高那張」決定高度
    let h = Math.round(w / safeAspect);

    // 下限 / 上限（避免過矮、也避免直式過長撐爆畫面）
    h = Math.max(DETAIL_IMAGE_MIN_H, h);

    const maxH = Math.round((window.innerHeight * DETAIL_IMAGE_MAX_VH) / 100);
    h = Math.min(h, Math.max(DETAIL_IMAGE_MIN_H, maxH));

    setFrameHeight(h);
  }, [enabled, minAspect]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    const el = ref.current;
    if (!el) return;

    // 初始算一次（非常重要：避免等 ResizeObserver 才算）
    requestAnimationFrame(() => compute());

    const ro = new ResizeObserver(() => compute());
    ro.observe(el);

    return () => ro.disconnect();
  }, [enabled, compute]);

  // minAspect 變了也要立即重算（避免「有算到最高圖但框沒變」）
  useEffect(() => {
    if (!enabled) return;
    requestAnimationFrame(() => compute());
  }, [enabled, minAspect, compute]);

  return { ref, frameHeight };
}

/**
 * 通用 iframe 播放器：
 * - list：高度固定 300px、寬度依比例、fit-content、靠左
 * - detail：寬度 100% 滿版、高度依比例縮放（aspect-ratio）
 */
function IframePlayer({
  src,
  title,
  variant,
  aspectRatio,
}: {
  src: string;
  title: string;
  variant: 'list' | 'detail';
  aspectRatio: number; // width / height
}) {
  const isList = variant === 'list';
  const listWidthPx = Math.round(LIST_PLAYER_HEIGHT * aspectRatio);

  return (
    <div
      style={{
        width: isList ? 'fit-content' : '100%',
        maxWidth: '100%',

        height: isList ? LIST_PLAYER_HEIGHT : 'auto',
        aspectRatio: isList ? undefined : `${aspectRatio} / 1`,

        overflow: 'hidden',
        borderRadius: isList ? 12 : 0,
        backgroundColor: '#000',

        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
      }}
    >
      <div
        style={{
          height: '100%',
          width: isList ? `${listWidthPx}px` : '100%',
          maxWidth: '100%',
        }}
      >
        <iframe
          src={src}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
          }}
        />
      </div>
    </div>
  );
}

/**
 * 本機短影片播放器：
 * - list：scroll 到超過一半高度時自動播放（靜音），離開停止
 * - detail：不做捲動判斷，一進頁就嘗試自動播放（靜音）
 * - detail：寬度滿版，高度依影片比例自動縮放（aspect-ratio）
 * - 自動播放時不顯示控制列；使用者點一下才顯示 controls
 * - 有 cover 時先顯示 cover，影片 ready 後淡入（降低黑畫面感）
 */
function ShortVideoPlayer({
  item,
  fallbackPoster,
  variant,
}: {
  item: ShortDataItem;
  fallbackPoster?: string;
  variant: 'list' | 'detail';
}) {
  const isList = variant === 'list';

  const [aspectRatio, setAspectRatio] = useState<number>(DEFAULT_SHORT_ASPECT);
  const [shouldPlay, setShouldPlay] = useState(false); // list 用
  const [showControls, setShowControls] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const posterUrl = item.cover || fallbackPoster || '';
  const hasPoster = !!posterUrl;

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (video.videoWidth && video.videoHeight) {
      const ratio = video.videoWidth / video.videoHeight;
      if (Number.isFinite(ratio) && ratio > 0) {
        setAspectRatio(ratio);
      }
    }
  };

  const handleCanPlay = () => {
    setIsReady(true);
  };

  // ✅ list：才做「捲到一半」判斷；detail：完全不做
  useEffect(() => {
    if (!isList) return;
    if (typeof window === 'undefined') return;

    const target = wrapperRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isMajorVisible = entry.isIntersecting && entry.intersectionRatio >= 0.6;
          setShouldPlay(isMajorVisible);
        });
      },
      { threshold: [0.2, 0.4, 0.6, 0.8, 1] }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isList]);

  // ✅ 播放策略：
  // - list：跟著 shouldPlay
  // - detail：mount 就直接 play()
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    el.muted = true;
    el.playsInline = true;

    if (isList) {
      if (shouldPlay) {
        const p = el.play();
        if (p && typeof p.then === 'function') p.catch(() => {});
      } else {
        el.pause();
      }
      return;
    }

    const p = el.play();
    if (p && typeof p.then === 'function') p.catch(() => {});

    return () => {
      el.pause();
    };
  }, [isList, shouldPlay]);

  const listWidthPx = Math.round(SHORT_PLAYER_HEIGHT * aspectRatio);

  return (
    <div
      ref={wrapperRef}
      style={{
        width: isList ? 'fit-content' : '100%',
        maxWidth: '100%',

        height: isList ? SHORT_PLAYER_HEIGHT : 'auto',
        aspectRatio: isList ? undefined : `${aspectRatio} / 1`,

        overflow: 'hidden',
        borderRadius: isList ? 12 : 0,
        backgroundColor: hasPoster ? '#000' : '#f3f3f3',
        backgroundImage: hasPoster ? `url(${posterUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',

        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
      }}
    >
      <div
        style={{
          height: '100%',
          width: isList ? `${listWidthPx}px` : '100%',
          maxWidth: '100%',
        }}
      >
        <video
          ref={videoRef}
          controls={showControls}
          playsInline
          muted
          preload="metadata"
          poster={hasPoster ? posterUrl : undefined}
          onLoadedMetadata={handleLoadedMetadata}
          onCanPlay={handleCanPlay}
          onClick={() => {
            if (!showControls) setShowControls(true);
          }}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: isList ? 'contain' : 'cover',
            opacity: isReady ? 1 : 0,
            transition: 'opacity 0.2s ease-out',
          }}
        >
          {item.video_url && <source src={item.video_url} />}
          您的瀏覽器不支援影片播放
        </video>
      </div>
    </div>
  );
}

/**
 * YouTube：
 * - list：高度固定 300、寬度 fit-content、靠左
 * - detail：寬度 100% 滿版、高度依 16:9 比例縮放
 */
function YouTubePlayer({
  baseUrl,
  title,
  variant,
}: {
  baseUrl: string;
  title: string;
  variant: 'list' | 'detail';
}) {
  const src = (() => {
    if (!baseUrl) return '';
    const hasQuery = baseUrl.includes('?');
    const params = 'rel=0&playsinline=1';
    return `${baseUrl}${hasQuery ? '&' : '?'}${params}`;
  })();

  return (
    <IframePlayer
      src={src}
      title={title || 'YouTube 影片'}
      variant={variant}
      aspectRatio={YOUTUBE_ASPECT}
    />
  );
}

/**
 * TikTok：
 * - list：高度固定 300、寬度 fit-content、靠左
 * - detail：寬度 100% 滿版、高度依 9:16 比例縮放（常見直向）
 */
function TikTokPlayer({
  embedUrl,
  title,
  variant,
}: {
  embedUrl: string;
  title: string;
  variant: 'list' | 'detail';
}) {
  return (
    <IframePlayer
      src={embedUrl}
      title={title || '抖音影片'}
      variant={variant}
      aspectRatio={TIKTOK_ASPECT}
    />
  );
}

export default function ArticleMediaCarousel({ article, variant, className }: Props) {
  const isList = variant === 'list';
  const swiperRef = useRef<SwiperType | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const updateOverflow = useCallback((sw: SwiperType) => {
    const containerW = sw.el?.clientWidth ?? 0;
    const wrapper = sw.wrapperEl as HTMLElement | null;
    const wrapperW = wrapper?.scrollWidth ?? 0;
    setHasOverflow(wrapperW > containerW + 2);
  }, []);

  // 圖片（排除心情圖）
  const images = getDisplayImages(article);

  // ✅ 收集「每張圖真實 aspect（w/h）」：用 onLoad 來拿，最穩
  const [detailAspectMap, setDetailAspectMap] = useState<Record<string, number>>({});

  // 最高的那張 = aspect 最小
  const detailMinAspect = useMemo(() => {
    const vals = Object.values(detailAspectMap).filter((v) => Number.isFinite(v) && v > 0);
    return vals.length ? Math.min(...vals) : 1;
  }, [detailAspectMap]);

  const detailImageFrame = useDetailImageFrame(variant === 'detail', detailMinAspect);

  const onDetailImageComplete = useCallback((src: string) => {
    // 參數 img 更改為 event
    return (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      // 從事件物件中提取圖片 DOM 元素
      const img = event.currentTarget;

      // 從圖片元素獲取 naturalWidth 和 naturalHeight
      const w = img?.naturalWidth ?? 0;
      const h = img?.naturalHeight ?? 0;

      if (!(w > 0 && h > 0)) return;

      const aspect = w / h;
      if (!Number.isFinite(aspect) || aspect <= 0) return;

      setDetailAspectMap((prev) => {
        // 避免無限 re-render
        if (prev[src] === aspect) return prev;
        return { ...prev, [src]: aspect };
      });
    };
  }, []);

  // ▶▶ 本機影片：在這裡直接解析 shortData
  const shortList: ShortDataItem[] = normalizeShortData(article.shortData);

  const hasShortData = shortList.length > 0;
  const hasImages = images.length > 0;
  const hasYoutube = !!(article.url && article.url.trim());
  const hasTiktok = !!(article.tiktokId && article.tiktokId.trim());
  const hasMusic = !!(article.music && article.music.trim());

  const mode = decideMediaMode(article, {
    hasImages,
    hasShortData,
    hasYoutube,
    hasTiktok,
    hasMusic,
  });

  if (!mode) return null;

  const title = article.title || '';

  /* ---------------- 圖片輪播（cover / covers） ---------------- */

  if (mode === 'image') {
    if (!hasImages) return null;

    if (isList) {
      // 只有一張圖：不要用 Swiper，避免 auto slide 造成寬度怪、按鈕也不用出現
      if (images.length === 1) {
        const src = images[0];

        return (
          <div className={className}>
            <div
              style={{
                width: 'fit-content',
                overflow: 'hidden',
                borderRadius: 12,
                background: '#f2f2f2',
              }}
            >
              <img
                src={src}
                alt={title ? `${title}（圖片 1）` : '文章圖片'}
                loading="lazy"
                style={{
                  maxWidth: '100%', // ✅ 跟著容器寬度
                  width: 'auto',
                  height: 'auto', // ✅ 等比例縮放（不固定高度）
                  maxHeight: '250px',
                  objectFit: 'contain', // 想裁切滿版就改成 cover（但 cover 通常要固定高才會裁切）
                }}
              />
            </div>
          </div>
        );
      }

      // 列表版：多張圖 side scroll（維持原本）
      return (
        <div className={`${className} ${styles.carousel}`}>
          <Swiper
            onSwiper={(sw) => (swiperRef.current = sw)}
            slidesPerView="auto"
            spaceBetween={8}
            modules={[FreeMode]}
            freeMode={{ enabled: true }}
            loop={false}
            observer
            observeParents
            navigation={hasOverflow}
            pagination={hasOverflow ? { clickable: true } : false}
            onInit={(sw) => updateOverflow(sw)}
            onResize={(sw) => updateOverflow(sw)}
            onUpdate={(sw) => updateOverflow(sw)}
          >
            {images.map((src, i) => (
              <SwiperSlide key={`${article.id}-img-${i}`} className={styles.autoSlide}>
                <LazyImage
                  src={src}
                  height={210}
                  width="auto"
                  aspect="auto"
                  autoAspectFallback="1/1"
                  imgStyle={{ objectFit: 'cover' }}
                  radius={12}
                  onLoad={() => {
                    const sw = swiperRef.current;
                    if (!sw) return;
                    sw.update();
                    updateOverflow(sw);
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      );
    }

    // ✅ 內頁版：框高由「最高那張」決定（min aspect），並且會在圖片載入後自動修正
    return (
      <div ref={detailImageFrame.ref} className={className}>
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={0}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
        >
          {images.map((src, i) => (
            <SwiperSlide key={`${article.id}-img-${i}`}>
              <div
                style={{
                  width: '100%',
                  height: detailImageFrame.frameHeight,
                  backgroundColor: '#f2f2f2',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Image
                  src={src}
                  alt={title ? `${title}（圖片 ${i + 1}）` : `文章圖片 ${i + 1}`}
                  fill
                  sizes="(min-width: 768px) 760px, 92vw"
                  style={{ objectFit: 'contain' }}
                  priority={i === 0}
                  onLoad={onDetailImageComplete(src)}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  /* ---------------- 本機影片輪播（shortData） ---------------- */

  if (mode === 'short') {
    if (!shortList.length) return null;

    return (
      <div className={className}>
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={isList ? 8 : 0}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
        >
          {shortList.map((item, index) => (
            <SwiperSlide key={`${article.id}-short-${item.short_id ?? index}`}>
              <ShortVideoPlayer item={item} fallbackPoster={images[0]} variant={variant} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  /* ---------------- YouTube 外連影片（url） ---------------- */

  if (mode === 'youtube') {
    const rawUrl = article.youtubeUrl?.trim() || article.url?.trim() || '';
    if (!rawUrl) return null;

    const id = extractYouTubeId(rawUrl);
    const embedSrc = id ? `https://www.youtube.com/embed/${id}` : rawUrl;

    return (
      <div className={className}>
        <YouTubePlayer baseUrl={embedSrc} title={title} variant={variant} />
      </div>
    );
  }

  /* ---------------- 抖音影片（tiktokId） ---------------- */

  if (mode === 'tiktok') {
    const rawId = article.tiktokId?.trim() ?? '';
    if (!rawId) return null;

    const embedUrl = buildTiktokEmbedUrl(rawId);

    return (
      <div className={className}>
        <TikTokPlayer embedUrl={embedUrl} title={title} variant={variant} />
      </div>
    );
  }

  /* ---------------- 音訊播放器（music） ---------------- */

  if (mode === 'music') {
    const isList = variant === 'list';
    const src = article.music?.trim() ?? '';
    if (!src) return null;

    return (
      <div className={`${isList ? '' : 'p-3'} ${className}`}>
        <div
          style={{
            width: '100%',
            padding: '16px 12px',
            borderRadius: 12,
            border: '1px solid rgba(0,0,0,0.06)',
            background: '#fafafa',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="fw-bold mb-1" style={{ fontSize: 14 }}>
              {title || article.music_name}
            </div>
            <audio controls style={{ width: '100%' }}>
              <source src={src} />
              您的瀏覽器不支援音訊播放
            </audio>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
